/* eslint-disable max-lines */
import {
  Component, OnInit, Input, Inject, OnChanges, SimpleChanges, AfterViewInit,
  ViewEncapsulation, OnDestroy, ChangeDetectorRef, AfterContentChecked
} from '@angular/core';
import { DatePipe, ViewportScroller } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl, Validators, UntypedFormBuilder } from '@angular/forms';
import { zip, Observable, iif, of, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { CookieService } from 'ngx-cookie-service';

import { isNullOrUndefined } from '@app/util/util';
import { environment } from '@environment';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { BookingBaseContainer } from '@booking/containers/booking-base.container';
import { AirValidationResponse, AirValidationResponseVm } from '@app/entities/air-validation.response';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { TravelDetailProvider } from '@shared/flight-itinerary/entities/travel-detail-provider';
import { BookingFacade } from '@booking/booking.facade';
import { BOOKING_FORM_INPUTS } from '@booking/constants/forms.constants';
import { BookingArrangementVM, SubmitBookingArrangement } from '@booking/entities/booking-arrangement';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { BillingFacade } from '@billing/billing.facade';
import { PaxFacade } from '@pax/pax.facade';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { LoyaltyCreateEvent, LoyaltyUpdateEvent } from '@pax/entities/pax-ui';
import { LoyaltyResponse } from '@pax/entities/traveler-pax';
import { PaxFormService } from '@pax/services/pax-form.service';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { HotelValidationResponse } from '@booking/entities/hotel-validation.response';
import { DateTransformPipe } from '@app/pipes/date-format.pipe';
import { formatStrinWithTimeZoneDateByRegex } from '@ux/mappers/date.mapper';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { packageBookingPayloadMapper } from '@booking/mappers/package-booking.payload.mapper';
import { AirPackagePricePipe } from '@booking/pipes/package-price.pipe';
import { ScrollFormService } from '@reservation/services/scroll-form.service';
import { PackageBookingDetailViewType } from '@packages/entities/booking-detail-view-type';
import { PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { StyleDots } from '@app/modules/carousel/entities/carousel-direction';
import { BookingAmplitudeService } from '@booking/services/booking-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { UpsellFacade } from '@upsell/upsell.facade';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { LuggagePipe } from '@shared/luggage/pipes/luggage.pipe';
import { UpsellBookingProps } from '@upsell/entities/upsell-booking-params';
import {
  bookingUpsellRoute, informativeBookingRoute, informativePaxCelebrateBookingRoute
} from '@booking/constants/booking-routes.constants';
import { AppFacade } from '@app/app.facade';
import { UpsellResponse } from '@shared/flight-itinerary/entities/upsell-flights';
import { FlightItem } from '@app/entities/flight-results';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { upsellDesktopSelectRoutes } from '@booking/mappers/upsell-desktop.mapper';
import { flightKeysMapper } from '@flight-results/mappers/flight-keys.mapper';
import { Price } from '@app/entities/price';
import { AssistanceFacade } from '@shared/travel-assistance/assistance.facade';
import { DEFAULT_PHONE_CONTROL, DEFAULT_EMAIL_CONTROL } from '@forms/contact-form/constants/contact-form.constants';
import { FullValidTravelersPipe } from '@pax/pipes/full-valid-travelers.pipe';
import { AvoService } from '@avo/services/avo.service';
import { TravelerListVmPipe } from '@pax/pipes/traveler-list-vm.pipe';
import { assistanceSearchAmplitudePropsMapper } from '@app/modules/core/mappers/assistance-amplitude.mapper';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { airPackagePropsFromValidation } from '@amplitude/mappers/package-amplitude.mapper';

@Component({
  selector: 'app-booking-package-air',
  templateUrl: './booking-package-air.container.html',
  styleUrls: ['../booking-base.container.sass', './booking-package-air.container.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})
export class BookingPackageAirContainer extends BookingBaseContainer implements OnInit,
  OnChanges, AfterViewInit, OnDestroy, AfterContentChecked {

  @Input() airValidation: AirValidationResponse;
  @Input() hotelValidation: HotelValidationResponse;

  public bookingPackageAirForm: UntypedFormGroup;
  public packageAirSummaryProvider: TravelDetailProvider = TravelDetailProvider.BOOKING_PACKAGE_AIR;
  public adults: TravelerTypes = TravelerTypes.ADULTS;
  public children: TravelerTypes = TravelerTypes.CHILDREN;
  public policiesType: SearchBy = SearchBy.PACKAGES;
  public styleDots: StyleDots = StyleDots.Home;
  public clicked: boolean = false;
  public selectedRoutes: FlightItem[] = [];
  public showUpsellsFormModal: boolean = false;

  private dateFormat: string = 'EEE, d MMM';
  private _allChildrenAges: number[][] = [];

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly datePipe: DatePipe,
    private readonly dateTransformPipe: DateTransformPipe,
    private readonly packagePrice: AirPackagePricePipe,
    private readonly upsellFacade: UpsellFacade,
    private readonly luggagePipe: LuggagePipe,
    private readonly domManipulateService: DomManipulateService,
    private readonly airPackagePricePipe: AirPackagePricePipe,
    private readonly cookieService: CookieService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected cdRef: ChangeDetectorRef,
    protected appFacade: AppFacade,
    protected facade: BookingFacade,
    protected viewportScroller: ViewportScroller,
    protected billingFacade: BillingFacade,
    protected paxFacade: PaxFacade,
    protected paxFormService: PaxFormService,
    protected scrollFormService: ScrollFormService,
    protected propertiesAmplitudeService: PropertiesAmplitudeService,
    protected bookingAmplitudeService: BookingAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected assistanceFacade: AssistanceFacade,
    protected fullValidTravelersPipe: FullValidTravelersPipe,
    protected avoService: AvoService,
    protected travelerListVmPipe: TravelerListVmPipe,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super(
      route, router, cdRef, appFacade, viewportScroller, facade,
      billingFacade, paxFacade, paxFormService, scrollFormService,
      propertiesAmplitudeService, bookingAmplitudeService, triggerAmplitudeService,
      assistanceFacade, fullValidTravelersPipe, avoService, travelerListVmPipe
    );
    this.travelerBookingProvider = TravelBookingType.PACKAGE_AIR;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['user'] && this.bookingPackageAirForm) {
      this.setUserData();
    }
  }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.initPackageAirBooking();
      this.listenToAssistanceSearchCall(
        this.airValidation, this.airValidation?.TripOption?.Price?.CurrencyCode, this.route.snapshot.paramMap
      )
    }
  }

  ngAfterContentChecked(): void {
    this.checkPaxSection();
  }

  ngAfterViewInit(): void {
    if (this.user && this.bookingPackageAirForm) this.setUserData();
  }

  ngOnDestroy(): void {
    this.assistanceFacade.resetAssistanceSearchInfo();
    if (!isNullOrUndefined(this.isLoadingBookingDelay$)) this.isLoadingBookingDelay$.unsubscribe();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  get isLoadingFligthsUpsellDesktop$(): Observable<boolean> {
    return this.upsellFacade.isLoadingUpsellForDesktop$;
  }

  get fligthsUpsellDesktop$(): Observable<UpsellResponse> {
    return this.upsellFacade.upsellForDesktop$;
  }

  get frequentFlyerPrograms$(): Observable<FrequentFlyerResponse[]> {
    return this.facade.frequentFlyerPrograms$;
  }

  get guestsPerRoomState$(): Observable<Room[]> {
    return this.facade.guestsPerRoomState$;
  }

  get passengerArrangementsState$(): Observable<PassengerArrangement[]> {
    return this.facade.passengerArrangementsState$;
  }

  get flightClass$(): Observable<FlightClass> {
    return this.facade.flightClass$;
  }

  get isLoadingUpsell$(): Observable<boolean> {
    return this.upsellFacade.isLoadingUpsell$;
  }

  get isUpsellEnabled(): boolean {
    return this.airValidation?.TripOption?.UpSellEnabled;
  }

  get isOneWayTripType(): boolean {
    return !!this.airValidation && !!this.airValidation.TripOption && !!this.airValidation.TripOption.Sectors &&
      this.airValidation.TripOption.Sectors.length === 1;
  }

  get hasAirValidation(): boolean {
    return !isEmpty(this.airValidation);
  }

  get hasHotelValidation(): boolean {
    return !isEmpty(this.hotelValidation);
  }

  get packageBookingDetailViewType(): typeof PackageBookingDetailViewType {
    return PackageBookingDetailViewType;
  }

  get upsellSelected(): boolean {
    return !isNullOrUndefined(this.upsellOptionId) && !isNullOrUndefined(this.upsellId);
  }

  get upsellOptionId(): number {
    const optionId = this.route.snapshot.paramMap.get(UpsellBookingProps.upsellOptionId);
    return !isNaN(Number(optionId)) ? Number(optionId) : null;
  }

  get fareFamilies(): string | null {
    if (this.airValidation?.TripOption?.Sectors?.length <= 0) {
      return null;
    }
    return this.airValidation?.TripOption?.Sectors[0]?.Flights[0]?.FareFamilies;
  }

  get validateBaggage(): boolean {
    return !!this.airValidation && !!this.airValidation?.TripOption?.Sectors && !!this.airValidation?.TripOption?.Sectors[0] &&
      !!this.airValidation?.TripOption?.Sectors[0].Flights && !!this.airValidation?.TripOption?.Sectors[0].Flights[0] &&
      !!this.airValidation?.TripOption?.Sectors[0].Flights[0].BaggageRestrictions;
  }

  get baggage(): boolean {
    if (this.validateBaggage) {
      const baggage = this.luggagePipe.transform(this.airValidation?.TripOption?.Sectors[0].Flights[0].BaggageRestrictions);
      return !isNullOrUndefined(baggage) && baggage !== 0 ? true : false;
    } else return false;
  }

  get airPackagePrice(): Price {
    return this.airPackagePricePipe.transform(this.airValidation, this.hotelValidation);
  }

  get airlineCode(): string {
    return this.airValidation?.TripOption?.Sectors[0]?.Flights[0]?.AirlineCode;
  }

  get airlineProvider(): string {
    return this.airValidation?.TripOption?.Provider;
  }

  get startDate(): string {
    return this.airValidation?.TripOption?.Sectors?.[0]?.Flights?.[0]?.DepartureDate;
  }

  private get allChildrenAges$(): Observable<number[][]> {
    return this.guestsPerRoomState$.pipe(
      map(rooms => [].concat(rooms.map(room => room.childrenAges)))
    );
  }

  private get upsellId(): string {
    const id = this.route.snapshot.paramMap.get(UpsellBookingProps.upsellId);
    return id && id.toLowerCase() !== 'null' ? id : null;
  }

  public childrenAgeByPackage(roomIndex: number, indexTypeNumber: number, type: TravelerTypes): number {
    if (type === TravelerTypes.ADULTS) {
      return null;
    } else {
      if (this._allChildrenAges?.length && this._allChildrenAges?.[roomIndex]?.length) {
        const roomAges = this._allChildrenAges[roomIndex];
        const first = roomAges[indexTypeNumber];
        return first;
      } else return null;
    }
  }

  public checkUpsell(): void {
    this.clicked = true;
    this.upsellFacade.fetchPackageUpsellOptions({
      SearchId: this.route.snapshot.paramMap.get('searchIdAir'),
      optionIdAir: this.route.snapshot.paramMap.get('optionIdAir'),
      rateId: this.route.snapshot.paramMap.get('rateIdHotel'),
      hotelOptionId: this.route.snapshot.paramMap.get('optionIdHotel'),
      SessionToken: this.cookieService.get('SeamlessLogin')
    });
    this.registerSelectUpsellOptions();
  }

  public createPackageLoyalty(loyalty: LoyaltyCreateEvent, roomId: number, guestId: number = null): void {
    if (!loyalty.create) {
      this.paxFacade.addPackageLoyaltyProgram(loyalty.loyalty, roomId, guestId);
    } else this.paxFacade.createPackageLoyaltyProgram(loyalty.loyalty, roomId, guestId);
  }

  public selectPackageLoyalty(loyalty: LoyaltyResponse, roomId: number, guestId: number = null): void {
    this.paxFacade.setPacakgeLoyaltyProgram(loyalty, roomId, guestId);
  }

  public updatePackageLoyalty(loyalty: LoyaltyUpdateEvent, roomId: number, guestId: number = null): void {
    if (!loyalty.update) this.paxFacade.addPackageLoyaltyProgram(loyalty.loyalty, roomId, guestId);
    else this.paxFacade.updatePacakgeLoyaltyProgram(loyalty.loyalty, roomId, guestId);
  }

  public deletePackageLoyalty(loyalty: LoyaltyResponse, roomId: number, guestId: number = null): void {
    this.paxFacade.deletePacakgeLoyaltyProgram(loyalty, roomId, guestId);
  }

  public dateStay(checkin: string, checkout: string): string {
    return `${this.formatDate(new Date(formatStrinWithTimeZoneDateByRegex(checkin)),
      this.dateFormat)} - ${this.formatDate(new Date(formatStrinWithTimeZoneDateByRegex(checkout)), this.dateFormat)}`;
  }

  public submitBookingOptions(isDisabled: boolean): void {
    zip(
      this.submitToBookServiceSubscription$(this.bookingPackageAirForm.value, null),
      this.propertiesAmplitudeService.searchProperties$(SearchBy.FLIGHTS).pipe(take(1)),
      this.assistanceFacade.assistanceSearchInfo$
    ).pipe(
      switchMap((([{ props, trigger }, searchProps, assistanceSearchInfo]) => (
        this.avoService.getEventTriggerProps$(
          AVO_EVENTS.VIEW_TRAVEL_ASSISTANCE_MODAL,
          assistanceSearchAmplitudePropsMapper(assistanceSearchInfo?.Results?.[0]),
          searchProps
        ).pipe(
          tap(eventProps => {
            if (this.isActivePaidAssistance ) {
              this.fetchAssistanceModal(this.bookingPackageAirForm, eventProps, isDisabled);
            } else {
              this.submitByBookServiceAction({ props, trigger }, this.bookingPackageAirForm, this.travelerBookingProvider);
            }
          })
        )
      )))
    ).subscribe().unsubscribe();
  }

  public submitBookingOptionsByAssistance(
    arrangement: BookingArrangementVM, assistanceInfo: { optionId: number, searchId: string }
  ): void {
    this.submitToBookServiceSubscription$(arrangement, assistanceInfo).pipe(
      tap(({ props, trigger }) => this.submitByBookServiceAction({ props, trigger },
        this.bookingPackageAirForm, this.travelerBookingProvider))
    ).subscribe().unsubscribe();
  }

  public showPackageDetail(
    data: { validator: AirValidationResponseVm, sectorNumber: number }, viewType: PackageBookingDetailViewType
  ): void {
    zip(this.passengerArrangementsState$, this.flightClass$, this.conversionRate$,
      this.isAnInternationalTrip$).pipe(
      take(1),
      map(([passengerArrangement, flightClass, conversionRate,isInternational]) => ({
        viewType, flightClass, passengerArrangement, conversionRate,
        packagePrice: this.packagePrice.transform(this.airValidation, this.hotelValidation),
        provider: data?.validator?.TripOption?.Provider,
        airValidationSector: data?.validator?.TripOption?.Sectors,
        hotelValidation: this.hotelValidation,
        airValidation: this.airValidation,
        sectorSelected: data.sectorNumber,
        travelDetailProvider: this.packageAirSummaryProvider,
        isInternational: isInternational,
        productReference: data.validator.TripOption.ProductReference,
      })),
      tap(state => this.router.navigate([this.bookingDetail], { state, relativeTo: this.route, skipLocationChange: true }))
    ).subscribe().unsubscribe();
  }

  public removeDefaultPax(roomIndex: number, guestId: number): void {
    this.paxFacade.removeDefaultPaxByGuestRoomAction(roomIndex, guestId);
  }

  public setShowBillingModule(value: boolean): void {
    this.showBillingModule = value;
    this.resetbillingForm(this.bookingPackageAirForm);
  }

  private submitToBookServiceSubscription$(
    arrangement: BookingArrangementVM, assistanceInfo: { optionId: number, searchId: string }
  ): Observable<SubmitBookingArrangement> {
    return zip(
      this.route.paramMap.pipe(take(1)), this.guestsPerRoomState$.pipe(take(1)),
      this.billingFacade.defaultBillingInfo$.pipe(take(1)),
      this.propertiesAmplitudeService.searchProperties$(SearchBy.PACKAGES).pipe(take(1)),
      this.bookingReady$.pipe(take(1))
    ).pipe(
      map(([params, rooms, defaultBillingInfo, searchProps, isReadyBook]) => ({
        params, rooms, defaultBillingInfo, searchProps,
        isReadyBook: !!this.bookingPackageAirForm.valid && !!this.hasAirValidation && !!this.hasHotelValidation && !!isReadyBook
      })),
      switchMap(({ isReadyBook, searchProps, params, defaultBillingInfo, rooms }) => iif(
        () => !!isReadyBook,
        zip(
          of(
            packageBookingPayloadMapper(
              this.bookingPackageAirForm,
              this.airValidation.ValidateOptionIdAir, this.hotelValidation?.ValidateOptionIdHotel,
              arrangement, defaultBillingInfo, rooms, assistanceInfo
            )
          ),
          this.bookingAmplitudeService.packagesCheckoutTriggerAmplitudeEntity$(this.hotelValidation,
            this.airValidation, params, searchProps).pipe(take(1)),
        ),
        of([null, null])
      )),
      map(([props, trigger]) => ({ props, trigger }))
    );
  }

  private formatDate(date: Date, format: string): string {
    return this.formatStringDate(this.datePipe.transform(date, format));
  }

  private formatStringDate(date: string): string {
    return this.dateTransformPipe.transform(date);
  }

  private createForm(): void {
    this.bookingPackageAirForm = new UntypedFormGroup({
      [BOOKING_FORM_INPUTS.TRAVELERS]: this.fb.array([]),
      [BOOKING_FORM_INPUTS.PHONE]: DEFAULT_PHONE_CONTROL,
      [BOOKING_FORM_INPUTS.EMAIL]: DEFAULT_EMAIL_CONTROL,
      [BOOKING_FORM_INPUTS.COMMENTS]: new UntypedFormControl(''),
      [BOOKING_FORM_INPUTS.TERMS]: new UntypedFormControl(null, [
        Validators.required, Validators.minLength(3)
      ]),
      [BOOKING_FORM_INPUTS.BILLING]: this.fb.group({})
    });
    if (this.individualTerms) {
      this.bookingPackageAirForm.addControl(BOOKING_FORM_INPUTS.EXTRA_TERMS, new UntypedFormControl(null, [
        Validators.required, Validators.minLength(3)
      ]));
    }
  }

  private setUserData(): void {
    this.bookingPackageAirForm.patchValue({
      [BOOKING_FORM_INPUTS.EMAIL]: this.user.email ? this.user.email : '',
      [BOOKING_FORM_INPUTS.PHONE]: this.user.phone ?
        `${this.user.country_code ? this.user.country_code.replace('+', '') :
          environment.countryInfo.phone_prefix.replace('+', '')}${this.user.phone}` : ''
    });
  }

  private initPackageAirBooking(): void {
    this.createForm();
    this.subscriptions.push(
      this.celebratePaxView$.pipe(
        distinctUntilChanged(),
        tap(celebratePaxView => {
          if (celebratePaxView) {
            this.router.navigate([{ outlets: { [informativeBookingRoute]: [informativePaxCelebrateBookingRoute] } }], {
              state: { celebratePaxView },
              relativeTo: this.route, skipLocationChange: true
            });
          }
        })
      ).subscribe()
    );
    this.isLoadingBookingDelay$ = this.isLoadingBookingDelaySubscription();
    this.listenFormChanges(this.bookingPackageAirForm);
    this.listenUpsell();
    this.listenUpsellDesktop();
    this.allChildrenAges$.pipe(
      tap(allChildrenAges => {
        this._allChildrenAges = allChildrenAges;
      })
    ).subscribe().unsubscribe();
    this.configDefaultByInfoType(this.travelerBookingProvider, this.startDate);
  }

  private listenUpsell(): void {
    this.subscriptions.push(
      combineLatest([this.isLoadingUpsell$, this.upsellFacade.upsell$]).pipe(
        map(([isLoading, data]) => ({ isLoading, data })),
        filter(({ isLoading }) => !isLoading),
        tap(({ data }) => {
          if (this.clicked) {
            if (!isEmpty(data)) {
              if (!data.UpsellOptions?.length) {
                this.clicked = false;
              } else {
                this.openUpsell();
              }
            }
          }
        })
      ).subscribe()
    );
  }

  private openUpsell(): void {
    this.clicked = false;
    this.router.navigate([{ outlets: { 'terms': [bookingUpsellRoute] } }], {
      state: {
        OptionId: Number(this.route.snapshot.paramMap.get('optionIdAir')),
        EventSource: EVENT_SOURCE.BOOKING,
        BasePrice: this.airValidation?.TripOption?.Price?.Total,
        FlightOptions: { options: this.airValidation?.TripOption?.Sectors, provider: this.airValidation?.TripOption?.Provider },
        isPackages: true,
        packageHotelSearchId: this.route.snapshot.paramMap.get('searchIdHotel'),
        packageHotelOptionId: Number(this.route.snapshot.paramMap.get('optionIdHotel')),
        packageHotelUpsell: {
          CheckinDate: this.hotelValidation?.HotelReservation?.Checkin,
          CheckoutDate: this.hotelValidation?.HotelReservation?.Checkout,
          HotelCode: this.hotelValidation?.HotelReservation?.Hotel?.HotelCode,
          HotelName: this.hotelValidation?.HotelReservation?.Hotel?.HotelName,
          Category: this.hotelValidation?.HotelReservation?.Hotel?.Category,
        }
      }, relativeTo: this.route, skipLocationChange: true
    });
  }

  public openUpsellDesktop(): void {
    this.registerSelectUpsellOptions();
    this.selectedRoutes = upsellDesktopSelectRoutes(this.airValidation?.TripOption?.Sectors);
    const flightKeys = flightKeysMapper(this.selectedRoutes);
    const optionId = this.airValidation?.TripOption?.OptionId;
    this.facade.selectFlightsPackagesUpsellForDesktop(
      {
        SearchId: this.route.snapshot.paramMap.get('searchIdAir'),
        SessionToken: this.cookieService.get('SeamlessLogin'),
        FlightKeys: flightKeys,
        AirlineCode: null,
        OptionId: optionId,
      },
      {
        SearchId: this.route.snapshot.paramMap.get('searchIdAir'),
        optionIdAir: null,
        hotelOptionId: this.route.snapshot.paramMap.get('optionIdHotel'),
        rateId: this.route.snapshot.paramMap.get('rateIdHotel'),
        SessionToken: this.cookieService.get('SeamlessLogin')
      }
    );
  }

  public toggleUpsellForm(show: boolean = false): void {
    this.showUpsellsFormModal = show; this.domManipulateService.setBodyScroll(!show);
  }

  public airPackageTotalPrice(airValidation: AirValidationResponse,hotelValidation: HotelValidationResponse): number {
    return this.airPackagePricePipe.transform(airValidation, hotelValidation)?.Total;
  }
  
  private listenUpsellDesktop(): void {
    this.subscriptions.push(
      combineLatest([this.isLoadingFligthsUpsellDesktop$, this.fligthsUpsellDesktop$]).pipe(
        filter(([isLoadingFligthsUpsell, fligthsUpsell]) => !isLoadingFligthsUpsell &&
          !isEmpty(fligthsUpsell) && !!fligthsUpsell.UpsellOptions),
        map(([isLoadingFligthsUpsell, fligthsUpsell]) => ({ isLoadingFligthsUpsell, fligthsUpsell })),
        tap(() => {
          this.toggleUpsellForm(true);
        })).subscribe());
  }

  private registerSelectUpsellOptions(): void {
    const productProps = airPackagePropsFromValidation(this.airValidation, this.hotelValidation);
    this.propertiesAmplitudeService.searchProperties$(SearchBy.PACKAGES).pipe(
      take(1),
      switchMap(searchProps => this.avoService.getEventTriggerProps$(
        AVO_EVENTS.SELECT_UPSELL_OPTIONS, null, searchProps, productProps
      ).pipe(
        take(1),
        tap(airPackageTrigger => {
          this.AvoDispatch.selectUpsellOptions({
            ...this.avoService.avoBaseProperties(airPackageTrigger),
            ...this.avoService.avoSearchProps(airPackageTrigger?.SEARCH_PROPS),
            ...this.avoService.avoProductProps(airPackageTrigger?.PRODUCT_PROPS),
          });
        })
      ))
    ).subscribe().unsubscribe();
  }
}
